import isEqual from 'lodash/isEqual';
import qs from 'qs';
import { useCallback, useMemo, useState } from 'react';
import { NavigateOptions, useLocation, useNavigate } from 'react-router';
import {
  CoachUsersOrder,
  DateRange,
  MembersListQueryVariables,
  OrderDirection,
} from '../graphql/types';
import { getLocalTimeUtcStartOfDay } from '../utils/date';
import { EventLabels } from '../utils/trackingEvents';

export const eventLabelFilterMap: Record<string, EventLabels> = {
  hasUnreadMessages: EventLabels.UnreadMessages,
  isNew: EventLabels.New,
  isStarred: EventLabels.Starred,
};

const primitiveDecoder = (
  str: string,
  _: qs.defaultDecoder,
  charset: string,
) => {
  // this whole block is just to decode booleans from strings
  // https://github.com/ljharb/qs/issues/91#issuecomment-437926409
  const strWithoutPlus = str.replace(/\+/g, ' ');
  if (charset === 'iso-8859-1') {
    // unescape never throws, no try...catch needed:
    return strWithoutPlus.replace(/%[0-9a-f]{2}/gi, unescape);
  }

  if (/^(\d+|\d*\.\d+)$/.test(str)) {
    return parseFloat(str);
  }

  const keywords: Record<string, true | false | null | undefined> = {
    true: true,
    false: false,
    null: null,
    undefined,
  };
  if (str in keywords) {
    return keywords[str];
  }

  // utf-8
  try {
    return decodeURIComponent(strWithoutPlus);
  } catch {
    return strWithoutPlus;
  }
};

export const useQueryParams = <T>(route: string) => {
  const location = useLocation();
  const navigate = useNavigate();

  // substring(1) to cut the "?" from the beginning of location.search
  const queryParams = useMemo(
    () =>
      qs.parse(location.search.substring(1), {
        decoder: primitiveDecoder,
      }) as Partial<T>,
    [location.search],
  );

  const setQueryParams = useCallback(
    (params?: Partial<T>, options?: NavigateOptions) => {
      const allParamsAreUndefined = Object.values(params ?? {}).every(
        (v) => v === undefined,
      );
      if (!params || allParamsAreUndefined) {
        const paramsAndOptions = [route, options].filter(Boolean);
        navigate(...(paramsAndOptions as [string, NavigateOptions]));
        return;
      }
      const paramsAndOptions = [
        `${route}?${qs.stringify(params, {})}`,
        options,
      ].filter(Boolean);
      navigate(...(paramsAndOptions as [string, NavigateOptions]));
    },
    [route, navigate],
  );

  return {
    queryParams,
    setQueryParams,
  };
};

export function useStateQueryParams<T>(
  route: string,
  initialState: T,
): [state: T, setState: (updates: Partial<T>) => void] {
  const [defaultState] = useState(initialState);
  const location = useLocation();
  const { queryParams, setQueryParams } = useQueryParams<T>(route);

  const updateState = useCallback(
    (nextState: Partial<T>) => {
      const nextQueryParams: Partial<T> | undefined = { ...queryParams };

      const nextStateKeys = Object.keys(nextState);
      const nextStateValues = Object.values(nextState);

      nextStateKeys.forEach((k, i) => {
        const key = k as keyof T;
        // if value is the same as the default value, reset it in the query params
        if (isEqual(nextState[key], defaultState[key])) {
          nextQueryParams[key] = undefined;
        } else {
          // otherwise add it to the query params
          const value: any = nextStateValues[i];
          nextQueryParams[key] = value;
        }
      });

      setQueryParams(nextQueryParams);
    },
    [defaultState, queryParams, setQueryParams],
  );

  const memoizedValues = useMemo(
    () => ({
      ...defaultState,
      ...queryParams,
    }),
    [defaultState, queryParams],
  );

  // watch for page being reset by clicking link to same page
  // e.g. user is on home page with some query params and user
  // clicks the link to the home page which has no params
  if (location.search === '') {
    return [memoizedValues, updateState];
  }

  return [memoizedValues, updateState];
}

export const defaultSessionDateRange = {
  start: getLocalTimeUtcStartOfDay().withFixedOffsetZone().toString(),
  end: getLocalTimeUtcStartOfDay()
    .plusDays(45)
    .withFixedOffsetZone()
    .toString(),
};

export type CoachSessionQueryVariables = {
  filters: string[];
  dateRange: DateRange;
  memberName?: string;
};

export const defaultCoachSessionQueryVariables: CoachSessionQueryVariables = {
  dateRange: defaultSessionDateRange,
  filters: [],
  memberName: undefined,
};

export const useCoachSessionQueryVariables = () =>
  useStateQueryParams<CoachSessionQueryVariables>(
    '/app/sessions',
    defaultCoachSessionQueryVariables,
  );

export const defaultFitMembersQueryParams = {
  filters: [],
  clientIds: [],
  memberName: undefined,
  orderBy: {
    order: CoachUsersOrder.LatestUserActivityStartTime,
    direction: OrderDirection.Desc,
  },
  pageSize: undefined,
};
export const useFitMembersQueryParams = () =>
  useStateQueryParams<{
    filters: string[];
    clientIds: string[];
    memberName?: string;
    orderBy: MembersListQueryVariables['orderBy'];
    pageSize?: number;
  }>('/app/members', defaultFitMembersQueryParams);

/* Filters for Classes Page */
export const defaultClassesDateRange = {
  start: getLocalTimeUtcStartOfDay().withFixedOffsetZone().toString(),
  end: getLocalTimeUtcStartOfDay().plusDays(7).withFixedOffsetZone().toString(),
};

type ClassesQueryVariables = {
  locationIds: string[];
  studioIds: string[];
  dateRange: DateRange;
};

export const defaultClassesQueryVariables: ClassesQueryVariables = {
  dateRange: defaultClassesDateRange,
  locationIds: [],
  studioIds: [],
};

export const useClassesQueryVariables = () =>
  useStateQueryParams<ClassesQueryVariables>(
    '/app/classes',
    defaultClassesQueryVariables,
  );
